import type { ComponentType, Dispatch } from 'react';

import { ChakraTabPanel, ChakraTabPanels } from '@equip.health/ui';

import { PAGE_LAYOUT } from '~/lib/constants';
import {
  type AssessmentAction,
  iframeContainerTabs,
  type PatientTabProps,
  type PatientTabsDefinition,
  PatientTabs,
} from '~/lib/constants/patients.constants';
import type { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import type { CompServTask } from '~/lib/util/task.util';

import PatientAppointmentsTab from './appointments';
import PatientActivityTab from './PatientActivityTab';
import { PatientAssessmentsRedesignTab } from './PatientAssessmentsTab';
import PatientClinicalProfileTab from './PatientClinicalProfileTab';
import PatientDocumentsTab from './PatientDocumentsTab';
import PatientEnrollmentTab from './PatientEnrollmentTab';
import PatientMessagesTab from './patientMessagesTab/PatientMessagesTab';
import PatientNotesTab from './patientNotesTab/PatientNotesTab';
import PatientOverviewTabSwitcher from './PatientOverviewTabSwitcher';
import PatientProfileTab from './PatientProfileTab';
import PatientSMTTab from './PatientSMTTab';
import PatientMedicationsTab from './medications/PatientMedicationsTab';
import PatientTasksTab from './PatientTasksTab';
import PatientLabsTab from './labs/PatientLabsTab';
import { PatientCarePlanTab } from '~/components/maud/patients/profile/tabs/careplan/PatientCarePlanTab';

export interface PatientTabPanelsProps {
  assessmentList: SurveyResponse[];
  detailsTabId: string;
  isAssessmentError: boolean;
  isAssessmentLoading: boolean;
  isTasksError: boolean;
  maudTaskCount?: number;
  onSendEmail: (assessment: Partial<SurveyResponse>) => void;
  onUpdateAssessmentStatus: (
    status: SurveyResponseStatus,
    toUserEmail: string,
    responseExternalId: string,
  ) => void;
  openTasks?: CompServTask[];
  patient: Patient;
  patientClinicalProfile: PatientClinicalProfile;
  patientDiagnoses: PatientDiagnosis[];
  patientEmail: string;
  patientFirstName: string;
  patientId: string;
  patientLastName: string;
  patientPhone: string;
  patientPreferredFullName: string;
  patientTasks?: Task[];
  selectedSurveyResults: Partial<SurveyResponse>;
  surveys: Survey[];
  familyTaskCount?: number;
  updateAssessmentState: Dispatch<AssessmentAction>;
  selectedTaskTab?: number;
  setSelectedTaskTab?: (selectedTaskTab: number) => void;
  tabsConfiguration: Record<string, PatientTabsDefinition>;
}

const PatientTabsMap: Record<PatientTabs, ComponentType<PatientTabProps>> = {
  [PatientTabs.OVERVIEW]: PatientOverviewTabSwitcher,
  [PatientTabs.PROFILE]: PatientProfileTab,
  [PatientTabs.CLINICAL_PROFILE]: PatientClinicalProfileTab,
  [PatientTabs.MEDICATIONS]: PatientMedicationsTab,
  [PatientTabs.LABS]: PatientLabsTab,
  [PatientTabs.JOURNAL]: PatientSMTTab,
  [PatientTabs.ASSESSMENTS]: PatientAssessmentsRedesignTab,
  [PatientTabs.ENROLLMENT]: PatientEnrollmentTab,
  [PatientTabs.CARE_PLAN]: PatientCarePlanTab,
  [PatientTabs.TASKS]: PatientTasksTab,
  [PatientTabs.APPOINTMENTS]: PatientAppointmentsTab,
  [PatientTabs.MESSAGES]: PatientMessagesTab,
  [PatientTabs.NOTES]: PatientNotesTab,
  [PatientTabs.DOCUMENTS]: PatientDocumentsTab,
  [PatientTabs.ACTIVITY]: PatientActivityTab,
};

const tabsWithNoRightPadding = [
  PatientTabs.ASSESSMENTS.toString(),
  PatientTabs.APPOINTMENTS.toString(),
  PatientTabs.ACTIVITY.toString(),
  PatientTabs.NOTES.toString(),
];

const PatientTabPanels = (props: PatientTabPanelsProps) => {
  const { tabsConfiguration } = props;

  return (
    <ChakraTabPanels height="100%">
      {Object.entries(PatientTabsMap)
        .filter(([tab]) => tabsConfiguration[tab])
        .map(
          ([tab, TabComponent]: [string, ComponentType<PatientTabProps>]) => {
            const rightPadding = tabsWithNoRightPadding.includes(tab)
              ? '0'
              : PAGE_LAYOUT.rightSpacing;

            const tabConfig = tabsConfiguration[tab];

            const shouldDisableOuterScroll = iframeContainerTabs.has(tab);

            return (
              <ChakraTabPanel
                aria-label={tabConfig.label}
                height="100%"
                key={`PatientTabPanels::${tabConfig.subPath}`}
                overflowX="hidden"
                overflowY={shouldDisableOuterScroll ? 'hidden' : 'auto'}
                padding="12px 0 0 0"
                paddingRight={rightPadding}
                width="100%"
              >
                <TabComponent {...props} />
              </ChakraTabPanel>
            );
          },
        )}
    </ChakraTabPanels>
  );
};

export default PatientTabPanels;
